import { useEffect, useState } from "react";

/** axe-core violation shape (subset we use for formatting). */
interface AxeViolation {
  id?: string;
  help?: string;
  nodes?: Array<{ failureSummary?: string }>;
}

/** axe-core run() result (subset). */
interface AxeResult {
  violations?: AxeViolation[];
}

function formatViolation(v: AxeViolation): string {
  const rule = v.id ?? "unknown";
  const help = v.help ?? "Accessibility issue";
  const count = v.nodes?.length ?? 0;
  const firstSummary = v.nodes?.[0]?.failureSummary;
  if (firstSummary) {
    return `Accessibility: ${rule}: ${help} — ${firstSummary}${count > 1 ? ` (+${count - 1} more)` : ""}`;
  }
  return `Accessibility: ${rule}: ${help} (${count} ${count === 1 ? "element" : "elements"})`;
}

export interface UseA11yWarningsResult {
  /** User-facing a11y warning strings (empty until axe has run, or when clean). */
  warnings: string[];
  /** True only after axe has run and settled (success or error). Used to show "passed" only when the check actually completed. */
  hasRun: boolean;
}

/**
 * Runs axe-core on the given DOM selector (in dev only) and returns violation messages
 * and a "has run" flag for the dev warnings panel. Only runs in the browser and when NODE_ENV is development.
 *
 * @param selector - CSS selector for the root to test (e.g. "#content", "main"). Pass null to skip.
 * @returns Object with warnings array and hasRun true only after axe has completed.
 */
export function useA11yWarnings(
  selector: string | null,
): UseA11yWarningsResult {
  const [warnings, setWarnings] = useState<string[]>([]);
  const [hasRun, setHasRun] = useState(false);

  useEffect(() => {
    const isDev = process.env.NODE_ENV === "development";
    if (!selector || typeof document === "undefined" || !isDev) {
      setWarnings([]);
      setHasRun(false);
      return;
    }

    setHasRun(false);
    let cancelled = false;
    let delayId: ReturnType<typeof setTimeout> | undefined;

    void import("axe-core").then(
      (axeModule: {
        default?: { run: (ctx: Element) => Promise<AxeResult> };
        run?: (ctx: Element) => Promise<AxeResult>;
      }) => {
        if (cancelled) {
          return;
        }
        const axe = axeModule.default ?? axeModule;
        const context = document.querySelector(selector);
        if (!context || typeof axe.run !== "function") {
          setWarnings([]);
          return;
        }

        const runFn = axe.run;
        const contextEl = context;

        /** Run axe after a short delay so lazy-rendered content (e.g. images) is in the DOM. */
        const runAxe = () => {
          if (cancelled) {
            return;
          }
          void runFn(contextEl)
            .then((results: AxeResult) => {
              if (cancelled) {
                return;
              }
              const messages = (results.violations ?? []).map(formatViolation);
              const forceFail =
                typeof window !== "undefined" &&
                window.location.search.includes("a11yFail=1");
              setWarnings(
                forceFail
                  ? [
                      ...messages,
                      "Accessibility: (simulated) Simulated failure for testing — add ?a11yFail=1 to the URL to see this.",
                    ]
                  : messages,
              );
              setHasRun(true);
            })
            .catch(() => {
              if (!cancelled) {
                setWarnings([]);
                setHasRun(true);
              }
            });
        };

        delayId = setTimeout(runAxe, 2000);
      },
    );

    return () => {
      cancelled = true;
      if (delayId !== undefined) {
        clearTimeout(delayId);
      }
    };
  }, [selector]);

  return { warnings, hasRun };
}
